<template>
  <div class="bg-surface rounded-xl px-3 pt-3 pb-4 aspect-3/4 flex flex-col">
    <div class="relative">
      <NuxtLink :to="createContentUrl(content.slug)">
        <img
          :src="getOptimizedImgUrl(content.imageUrl)"
          alt="Content preview image"
          class="rounded-lg aspect-3/2 mx-auto object-cover"
        />
      </NuxtLink>
      <ContentTypeIcon
        :content-type="content.type"
        class="absolute top-2 left-2"
      />
      <div v-if="content.isCustomizable" class="absolute bottom-2 left-2">
        <Chip
          :size="ChipSize.SM"
          :variant="ChipVariant.FILLED"
          :icon="SettingsIcon"
          :color="ChipColor.SECONDARY"
        >
          {{ $t('content.customizable') }}
        </Chip>
      </div>
    </div>
    <div class="px-2 flex-1">
      <NuxtLink
        :to="createContentUrl(content.slug)"
        class="font-bold text-lg leading-6 mt-3 text-on-surface line-clamp-2"
      >
        {{ content.title }}
      </NuxtLink>
      <div class="text-sm mt-1 text-on-surface-variant leading-5 line-clamp-2">
        {{ content.description }}
      </div>
    </div>
    <div class="flex justify-between flex-wrap gap-2 pt-2">
      <btn
        :color="BtnColor.PRIMARY"
        :variant="BtnVariant.FILLED"
        :size="BtnSize.MD"
        :to="createContentUrl(content.slug)"
      >
        {{ $t('hub.explore') }}
      </btn>
      <LaunchMetricChip :content="content" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { ContentTypeIcon } from '~/components/content-type-icon';
import { ContentDto } from '~/api/contents';
import { useOrganizationDomain } from '~/composables/use-organization-domain';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'nuxt/app';
import imageProxyBuilder, { ResizeType } from '@bitpatty/imgproxy-url-builder';
import { useRuntimeConfig } from '#imports';
import { LaunchMetricChip } from '~/pages/_components/content-card/_components/launch-metric-chip';
import { Chip, ChipColor, ChipSize, ChipVariant } from '~/components/chip';
import SettingsIcon from '~/assets/icons/settings.svg?component';

type Props = {
  content: ContentDto;
  contentUrl?: string;
};

const props = defineProps<Props>();

const config = useRuntimeConfig();
const route = useRoute();
const organizationDomain = useOrganizationDomain();
const { organization } = storeToRefs(useOrganizationStore());
const hubSlug = computed<string>(() => route.params.hubslug as string);

const createContentUrl = (contentSlug: string) => {
  if (props.contentUrl) {
    return props.contentUrl;
  }

  if (organizationDomain !== null || !organization.value) {
    return `/${hubSlug.value}/${contentSlug}`;
  }
  return `/org/${organization.value.slug}/${hubSlug.value}/${contentSlug}`;
};

const getOptimizedImgUrl = (url: string): string =>
  imageProxyBuilder()
    .resize({ type: ResizeType.FILL_DOWN, width: 480, height: 320 })
    .format('webp')
    .build({
      baseUrl: config.public.imgProxyBaseUrl || '',
      path: url,
      addExtension: true,
    });
</script>
